import type { BriefQuestion, ProjectBrief, Task } from '@dyspatch/core';
import { CircleHelp } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Surface } from '@/components/ui/section';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import { ago, minutesSince } from '@/lib/format';
import { openQuestions } from '@/lib/questions';

/**
 * What the planner was unsure about, and what it did about it
 * (docs/PLAN-QUESTIONS.md).
 *
 * The thing this region exists to stop: an agent reads a brief that can be read
 * two ways, picks one silently, and the person approving the plan finds out
 * forty minutes of agent time later at PR review. It could not ask - a planning
 * run has no stdin and the hub never calls a model - so the plan is the
 * question. Each card is a fork the planner hit, the reading it planned on, and
 * why the code did not settle it.
 *
 * **Answering is never required.** Go accepts every open assumption and says so
 * on the record, which is why nothing here is drawn as a gate: no red, no
 * count in the waiting figure, no button the page insists on. A person who
 * reads three questions and presses Go has made three decisions, and the specs
 * will say so.
 *
 * Risks and out-of-scope items render in the same region with no answer slot.
 * The hub has stored both since the first decomposition and no page has ever
 * drawn either, so a planner's whole vocabulary for doubt has been invisible;
 * they are the same kind of thing as a question, minus anybody being able to
 * settle them in a sentence.
 */
function AffectedChips({
  affects,
  tasks,
  onSelect,
}: {
  affects: readonly string[];
  tasks: readonly Task[];
  onSelect: (taskId: string) => void;
}) {
  if (!affects.length) return null;
  return (
    <span className="flex flex-wrap items-center gap-1">
      <span className="text-fg-3 text-2xs">changes</span>
      {affects.map((key) => {
        const target = tasks.find((task) => task.key === key);
        // A key with no task is a step edited away after the plan landed. It
        // stays on the card as plain text: the question still says what it
        // depends on, it just has nothing left to scroll to.
        if (!target) {
          return (
            <span key={key} className="bg-raised text-fg-3 rounded-sm px-1 font-mono text-2xs">
              {key}
            </span>
          );
        }
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(target.id)}
            className="bg-raised text-fg-2 hover:text-foreground rounded-sm px-1 font-mono text-2xs transition-colors duration-100"
            title={target.title}
          >
            {key}
          </button>
        );
      })}
    </span>
  );
}

function AnsweredLine({ question }: { question: BriefQuestion }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
      <span className="bg-raised rounded-sm px-1 font-mono text-2xs">{question.key}</span>
      <span className="text-foreground">{question.answer}</span>
      <span className="text-fg-3 text-2xs">
        {question.answeredBy}
        {question.answeredAt ? ` · ${ago(minutesSince(question.answeredAt))}` : ''}
      </span>
    </div>
  );
}

function QuestionCard({
  briefId,
  question,
  tasks,
  answerable,
  onSelect,
}: {
  briefId: string;
  question: BriefQuestion;
  tasks: readonly Task[];
  answerable: boolean;
  onSelect: (taskId: string) => void;
}) {
  const [typed, setTyped] = useState('');
  const answer = useHubAction(
    (text: string) => api.answerQuestion(briefId, question.key, text),
    // The card stays on screen and turns into its own answer, so a refusal
    // belongs under it rather than in a toast that outlives the box.
    { onDone: () => setTyped(''), inlineError: true },
  );

  // An answered card collapses: the question is settled, and the page below it
  // is the plan the answer is about.
  if (question.answer) {
    return (
      <li className="px-gutter py-2.5">
        <AnsweredLine question={question} />
      </li>
    );
  }

  return (
    <li className="px-gutter py-3">
      <div className="flex items-start gap-2">
        <CircleHelp className="text-fg-3 mt-0.5 size-3.5 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-foreground max-w-[88ch] text-sm">
            <span className="bg-raised mr-1.5 rounded-sm px-1 font-mono text-2xs">
              {question.key}
            </span>
            {question.question}
          </p>
          {/* The assumption, not the question, is what a reader has to check:
              it is what the plan above them already says. */}
          <p className="text-fg-2 mt-1 max-w-[88ch] text-sm">
            Planned as if: <span className="text-foreground">{question.assumed}</span>
          </p>
          <p className="text-fg-3 mt-1 max-w-[88ch] text-xs">{question.why}</p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <AffectedChips affects={question.affects} tasks={tasks} onSelect={onSelect} />
            {question.acceptedAt ? (
              <span className="text-fg-3 text-2xs">
                accepted at Go · {ago(minutesSince(question.acceptedAt))}
              </span>
            ) : null}
          </div>

          {answerable ? (
            <>
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                {/* Keep is an answer, not a dismissal. Pressing it records that
                    somebody read the fork and chose the reading the plan was
                    written on, which is a different fact from nobody having
                    looked, and the note it files says so in the spec. */}
                <Button
                  size="xs"
                  variant="outline"
                  disabled={answer.busy}
                  onClick={() => answer.run(question.assumed)}
                >
                  Keep
                </Button>
                {question.options.map((option) => (
                  <Button
                    key={option}
                    size="xs"
                    variant="subtle"
                    disabled={answer.busy}
                    onClick={() => answer.run(option)}
                  >
                    {option}
                  </Button>
                ))}
              </div>
              <form
                className="mt-2 flex max-w-[60ch] items-center gap-1.5"
                onSubmit={(event) => {
                  event.preventDefault();
                  const text = typed.trim();
                  if (!text) return;
                  answer.run(text);
                }}
              >
                <Input
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  maxLength={2000}
                  placeholder="Or answer in your own words…"
                  aria-label={`Answer ${question.key}`}
                  className="flex-1"
                />
                <Button
                  type="submit"
                  size="sm"
                  variant="outline"
                  disabled={answer.busy || !typed.trim()}
                >
                  {answer.busy ? 'Saving…' : 'Answer'}
                </Button>
              </form>
            </>
          ) : null}

          {answer.error ? (
            <p className="text-bad pt-1.5 text-xs" role="alert">
              {answer.error}
            </p>
          ) : null}
        </div>
      </div>
    </li>
  );
}

export function PlanQuestions({
  brief,
  questions,
  tasks,
  answerable,
  onSelect,
}: {
  brief: ProjectBrief;
  questions: readonly BriefQuestion[];
  tasks: readonly Task[];
  /** Before Go only: after it the specs are frozen and this region is history. */
  answerable: boolean;
  onSelect: (taskId: string) => void;
}) {
  const risks = brief.risks ?? [];
  const outOfScope = brief.outOfScope ?? [];
  if (!questions.length && !risks.length && !outOfScope.length) return null;

  // Open first: an answered card is a decision already made, and the reason to
  // look at this region at all is the ones that are not.
  const open = openQuestions(questions);
  const settled = questions.filter((question) => question.answer);

  return (
    <Surface>
      {questions.length ? (
        <ul className="divide-line divide-y">
          {[...open, ...settled].map((question) => (
            <QuestionCard
              key={question.key}
              briefId={brief.id}
              question={question}
              tasks={tasks}
              answerable={answerable}
              onSelect={onSelect}
            />
          ))}
        </ul>
      ) : null}

      {risks.length ? (
        <div className="border-t px-gutter py-3">
          <div className="dy-eyebrow">Risks the planner named</div>
          <ul className="text-fg-2 mt-1.5 max-w-[88ch] list-disc space-y-1 pl-4 text-sm">
            {risks.map((risk, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: read-only, never reordered.
              <li key={`${index}-${risk}`}>{risk}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {outOfScope.length ? (
        <div className="border-t px-gutter py-3">
          <div className="dy-eyebrow">Left out on purpose</div>
          <ul className="text-fg-2 mt-1.5 max-w-[88ch] list-disc space-y-1 pl-4 text-sm">
            {outOfScope.map((item, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: read-only, never reordered.
              <li key={`${index}-${item}`}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </Surface>
  );
}
